import { useCallback, useEffect, useRef, useState } from "react";
import {
  Check,
  Image as ImageIcon,
  Loader2,
  Pencil,
  RefreshCw,
  Search,
  Trash2,
  UploadCloud,
  X,
} from "lucide-react";
import { api } from "../../services/api";
import { resolveProductImage } from "../../utils/productAssets";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

function formatBytes(size = 0) {
  const bytes = Number(size || 0);
  if (!bytes) return "Size unknown";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function uniqueByUrl(items = []) {
  const seen = new Set();
  return items.filter((item) => {
    const key = item?.url || item;
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export default function MediaLibraryPicker({
  buttonClassName = "",
  buttonLabel = "Open media library",
  folder = "media",
  multiple = false,
  onSelect,
  selectedUrls = [],
  source = "media_library",
}) {
  const [open, setOpen] = useState(false);
  const [assets, setAssets] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1, hasNextPage: false });
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [savingMeta, setSavingMeta] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState("");
  const [activeAsset, setActiveAsset] = useState(null);
  const [selected, setSelected] = useState(() => new Set(selectedUrls));
  const [uploadForm, setUploadForm] = useState({
    title: "",
    caption: "",
    description: "",
    altText: "",
  });
  const [metaForm, setMetaForm] = useState({
    title: "",
    caption: "",
    description: "",
    altText: "",
  });
  const searchInputRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set(selectedUrls));
  }, [open, selectedUrls]);

  useEffect(() => {
    if (!activeAsset) return;
    setMetaForm({
      title: activeAsset.title || "",
      caption: activeAsset.caption || "",
      description: activeAsset.description || "",
      altText: activeAsset.altText || "",
    });
  }, [activeAsset]);

  const loadAssets = useCallback(async ({ page = 1, append = false, refresh = false } = {}) => {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        page: String(page),
        limit: "36",
      });
      if (search.trim()) params.set("search", search.trim());
      if (refresh) params.set("refresh", "true");

      const data = await api.get(`/media/assets?${params.toString()}`);
      const nextAssets = Array.isArray(data.assets) ? data.assets : [];
      setAssets((current) => uniqueByUrl(append ? [...current, ...nextAssets] : nextAssets));
      setPagination(data.pagination || { page, totalPages: 1, hasNextPage: false });
      setActiveAsset((current) => current || nextAssets[0] || null);
    } catch (err) {
      setError(err.message || "Could not load media library");
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    if (!open) return;
    const timer = window.setTimeout(() => loadAssets({ page: 1 }), 250);
    return () => window.clearTimeout(timer);
  }, [loadAssets, open]);

  useEffect(() => {
    if (!open) return;
    window.setTimeout(() => searchInputRef.current?.focus(), 50);
  }, [open]);

  const toggleAsset = (asset) => {
    setActiveAsset(asset);
    setSelected((current) => {
      if (!multiple) return new Set([asset.url]);

      const next = new Set(current);
      if (next.has(asset.url)) next.delete(asset.url);
      else next.add(asset.url);
      return next;
    });
  };

  const confirmSelection = () => {
    const urls = [...selected];
    if (!urls.length && activeAsset?.url) urls.push(activeAsset.url);
    if (!urls.length) return;

    onSelect?.(multiple ? urls : urls[0]);
    setOpen(false);
  };

  const uploadFiles = async (event) => {
    const files = Array.from(event.target.files || []);
    event.target.value = "";
    if (!files.length) return;

    setUploading(true);
    setError("");
    try {
      const uploaded = [];
      for (const file of files) {
        const dataUrl = await fileToDataUrl(file);
        const data = await api.post("/media/assets", {
          ...uploadForm,
          dataUrl,
          fileName: file.name,
          folder,
          source,
          title: uploadForm.title || file.name.replace(/\.[^.]+$/, ""),
        });
        if (data.asset) uploaded.push(data.asset);
      }

      setAssets((current) => uniqueByUrl([...uploaded, ...current]));
      if (uploaded[0]) {
        setActiveAsset(uploaded[0]);
        setSelected((current) => {
          const next = multiple ? new Set(current) : new Set();
          uploaded.forEach((asset) => next.add(asset.url));
          return next;
        });
      }
      setUploadForm({ title: "", caption: "", description: "", altText: "" });
    } catch (err) {
      setError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const saveMetadata = async () => {
    if (!activeAsset?._id) return;
    setSavingMeta(true);
    setError("");
    try {
      const data = await api.patch(`/media/assets/${activeAsset._id}`, metaForm);
      const updated = data.asset;
      setActiveAsset(updated);
      setAssets((current) => current.map((asset) => (asset._id === updated._id ? updated : asset)));
    } catch (err) {
      setError(err.message || "Could not save media details");
    } finally {
      setSavingMeta(false);
    }
  };

  const deleteFromLibrary = async () => {
    if (!activeAsset?._id) return;
    if (!window.confirm("Remove this image from the media library? Existing product/category/CMS image URLs will not be deleted.")) return;

    try {
      await api.delete(`/media/assets/${activeAsset._id}`);
      setAssets((current) => current.filter((asset) => asset._id !== activeAsset._id));
      setSelected((current) => {
        const next = new Set(current);
        next.delete(activeAsset.url);
        return next;
      });
      setActiveAsset(null);
    } catch (err) {
      setError(err.message || "Could not remove media asset");
    }
  };

  const refreshLibrary = async () => {
    setRefreshing(true);
    setError("");
    try {
      await api.post("/media/assets/refresh", {});
      await loadAssets({ page: 1 });
    } catch (err) {
      setError(err.message || "Could not refresh media library");
    } finally {
      setRefreshing(false);
    }
  };

  const selectedCount = selected.size;
  const primaryButtonClass = buttonClassName || "inline-flex items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600";

  return (
    <>
      <button className={primaryButtonClass} onClick={() => setOpen(true)} type="button">
        <ImageIcon size={16} />
        {buttonLabel}
      </button>

      {open && (
        <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-gray-950/60 p-3 backdrop-blur-sm" role="dialog" aria-modal="true">
          <div className="flex max-h-[94vh] w-full max-w-7xl flex-col overflow-hidden rounded-3xl bg-white shadow-2xl">
            <header className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-100 px-5 py-4">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-[#4391d1]">Media Library</p>
                <h2 className="text-xl font-black text-gray-900">Select or upload image</h2>
              </div>
              <div className="flex items-center gap-2">
                <button className="inline-flex items-center gap-2 rounded-xl border border-gray-200 px-3 py-2 text-xs font-black text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1]" disabled={refreshing} onClick={refreshLibrary} type="button">
                  <RefreshCw className={refreshing ? "h-4 w-4 animate-spin" : "h-4 w-4"} />
                  Refresh existing
                </button>
                <button className="flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 text-gray-500 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={() => setOpen(false)} type="button">
                  <X size={18} />
                </button>
              </div>
            </header>

            {error && (
              <div className="border-b border-red-100 bg-red-50 px-5 py-3 text-sm font-bold text-red-600">
                {error}
              </div>
            )}

            <div className="grid min-h-0 flex-1 lg:grid-cols-[300px_1fr_340px]">
              <aside className="border-b border-gray-100 bg-gray-50 p-4 lg:border-b-0 lg:border-r">
                <section className="rounded-2xl border border-gray-100 bg-white p-4">
                  <p className="text-sm font-black text-gray-900">Upload new image</p>
                  <div className="mt-3 grid gap-2">
                    <input className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setUploadForm((current) => ({ ...current, title: event.target.value }))} placeholder="Image title" value={uploadForm.title} />
                    <input className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setUploadForm((current) => ({ ...current, altText: event.target.value }))} placeholder="Alt text" value={uploadForm.altText} />
                    <input className="rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setUploadForm((current) => ({ ...current, caption: event.target.value }))} placeholder="Caption" value={uploadForm.caption} />
                    <textarea className="min-h-20 rounded-xl border border-gray-200 px-3 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setUploadForm((current) => ({ ...current, description: event.target.value }))} placeholder="Description" value={uploadForm.description} />
                  </div>
                  <label className="mt-3 flex cursor-pointer items-center justify-center gap-2 rounded-xl bg-[#4391d1] px-4 py-3 text-sm font-black text-white hover:bg-blue-600">
                    {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <UploadCloud size={16} />}
                    {uploading ? "Uploading..." : multiple ? "Upload images" : "Upload image"}
                    <input accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" disabled={uploading} multiple={multiple} onChange={uploadFiles} type="file" />
                  </label>
                </section>
              </aside>

              <main className="min-h-0 overflow-hidden p-4">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <input ref={searchInputRef} className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-3 pl-10 pr-4 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setSearch(event.target.value)} placeholder="Search title, caption, filename..." value={search} />
                </div>

                <div className="mt-4 max-h-[58vh] overflow-y-auto pr-1">
                  {loading && !assets.length ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                      {Array.from({ length: 12 }).map((_, index) => (
                        <div className="aspect-square animate-pulse rounded-2xl bg-gray-100" key={index} />
                      ))}
                    </div>
                  ) : assets.length ? (
                    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-4">
                      {assets.map((asset) => {
                        const isSelected = selected.has(asset.url);
                        const isActive = activeAsset?._id === asset._id;
                        return (
                          <button
                            className={`group relative overflow-hidden rounded-2xl border bg-white text-left transition ${isSelected ? "border-[#4391d1] ring-4 ring-blue-100" : isActive ? "border-gray-300" : "border-gray-100 hover:border-blue-200"}`}
                            key={asset._id || asset.url}
                            onClick={() => toggleAsset(asset)}
                            type="button"
                          >
                            <div className="flex aspect-square items-center justify-center bg-gray-50">
                              <img alt={asset.altText || asset.title || asset.fileName} className="h-full w-full object-contain p-2" src={resolveProductImage(asset.url)} />
                            </div>
                            <div className="border-t border-gray-100 px-3 py-2">
                              <p className="truncate text-xs font-black text-gray-800">{asset.title || asset.fileName}</p>
                              <p className="mt-0.5 truncate text-[11px] font-bold text-gray-400">{asset.fileName}</p>
                            </div>
                            {isSelected && (
                              <span className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-[#4391d1] text-white shadow">
                                <Check size={15} />
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50 px-5 py-12 text-center">
                      <ImageIcon className="mx-auto h-10 w-10 text-gray-300" />
                      <p className="mt-3 text-sm font-black text-gray-700">No media found</p>
                      <p className="mt-1 text-xs font-semibold text-gray-400">Upload an image or refresh existing uploads.</p>
                    </div>
                  )}

                  {pagination.hasNextPage && (
                    <button className="mt-4 w-full rounded-2xl border border-gray-200 px-4 py-3 text-sm font-black text-gray-600 hover:border-[#4391d1] hover:text-[#4391d1]" disabled={loading} onClick={() => loadAssets({ page: Number(pagination.page || 1) + 1, append: true })} type="button">
                      {loading ? "Loading..." : "Load more"}
                    </button>
                  )}
                </div>
              </main>

              <aside className="min-h-0 border-t border-gray-100 bg-gray-50 p-4 lg:border-l lg:border-t-0">
                {activeAsset ? (
                  <div className="flex h-full flex-col">
                    <div className="rounded-2xl border border-gray-100 bg-white p-3">
                      <div className="flex aspect-square items-center justify-center rounded-xl bg-gray-50">
                        <img alt={activeAsset.altText || activeAsset.title || "Selected media"} className="h-full w-full object-contain p-2" src={resolveProductImage(activeAsset.url)} />
                      </div>
                      <p className="mt-3 break-all text-xs font-bold text-gray-500">{activeAsset.url}</p>
                      <p className="mt-1 text-xs font-bold text-gray-400">{formatBytes(activeAsset.size)} / {activeAsset.mimeType || "image"}</p>
                    </div>

                    <div className="mt-4 grid gap-2">
                      <input className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setMetaForm((current) => ({ ...current, title: event.target.value }))} placeholder="Title" value={metaForm.title} />
                      <input className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setMetaForm((current) => ({ ...current, altText: event.target.value }))} placeholder="Alt text" value={metaForm.altText} />
                      <input className="rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setMetaForm((current) => ({ ...current, caption: event.target.value }))} placeholder="Caption" value={metaForm.caption} />
                      <textarea className="min-h-24 rounded-xl border border-gray-200 bg-white px-3 py-2 text-sm font-semibold outline-none focus:border-[#4391d1]" onChange={(event) => setMetaForm((current) => ({ ...current, description: event.target.value }))} placeholder="Description" value={metaForm.description} />
                    </div>

                    <div className="mt-3 grid gap-2">
                      <button className="inline-flex flex-1 items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 text-xs font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" disabled={savingMeta} onClick={saveMetadata} type="button">
                        {savingMeta ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil size={14} />}
                        Save details
                      </button>
                      <button className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-100 bg-red-50 px-3 py-2 text-xs font-black text-red-600 hover:border-red-200 hover:bg-red-100" onClick={deleteFromLibrary} type="button">
                        <Trash2 size={14} />
                        Delete from media library
                      </button>
                    </div>
                    <p className="mt-2 text-[11px] font-semibold leading-5 text-gray-400">
                      Delete removes this media record from the library only. Existing website image URLs are left untouched.
                    </p>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-5 py-10 text-center text-sm font-bold text-gray-400">
                    Select an image to view details.
                  </div>
                )}
              </aside>
            </div>

            <footer className="flex flex-col-reverse gap-3 border-t border-gray-100 bg-white px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
              <p className="text-xs font-bold text-gray-400">
                {selectedCount ? `${selectedCount} selected` : activeAsset ? "Image selected for editing" : "Select an image to continue"}
              </p>
              <div className="flex flex-wrap justify-end gap-2">
                <button
                  className="inline-flex items-center justify-center gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm font-black text-red-600 hover:border-red-200 hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                  disabled={!activeAsset?._id}
                  onClick={deleteFromLibrary}
                  type="button"
                >
                  <Trash2 size={15} />
                  Delete
                </button>
                <button className="rounded-xl border border-gray-200 px-5 py-3 text-sm font-black text-gray-700 hover:border-[#4391d1] hover:text-[#4391d1]" onClick={() => setOpen(false)} type="button">
                  Cancel
                </button>
                <button className="rounded-xl bg-gray-950 px-5 py-3 text-sm font-black text-white disabled:cursor-not-allowed disabled:opacity-40" disabled={!selectedCount && !activeAsset?.url} onClick={confirmSelection} type="button">
                  Use selected
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </>
  );
}
